"use strict";
import { positionsLine } from "components/Charts/VolumeChart/CrosshairHighlightPrimitive";
import { positionsBox } from "components/Charts/VolumeChart/utils";
import { roundRect } from "components/Charts/utils";
export class RoundedCandleSeriesRenderer {
  _data = null;
  _options = null;
  draw(target, priceConverter) {
    target.useBitmapCoordinateSpace((scope) => this._drawImpl(scope, priceConverter));
  }
  update(data, options) {
    this._data = data;
    this._options = options;
  }
  _drawImpl(renderingScope, priceToCoordinate) {
    if (this._data === null || this._data.bars.length === 0 || this._data.visibleRange === null || this._options === null) {
      return;
    }
    let lastClose = -Infinity;
    const bars = this._data.bars.map((bar) => {
      const isUp = bar.originalData.close >= lastClose;
      lastClose = bar.originalData.close;
      const openY = priceToCoordinate(bar.originalData.open) ?? 0;
      const highY = priceToCoordinate(bar.originalData.high) ?? 0;
      const lowY = priceToCoordinate(bar.originalData.low) ?? 0;
      const closeY = priceToCoordinate(bar.originalData.close) ?? 0;
      return {
        openY,
        highY,
        lowY,
        closeY,
        x: bar.x,
        isUp
      };
    });
    const radius = this._options.radius(this._data.barSpacing);
    this._drawWicks({
      renderingScope,
      bars,
      visibleRange: this._data.visibleRange
    });
    this._drawCandles({
      renderingScope,
      bars,
      visibleRange: this._data.visibleRange,
      radius
    });
  }
  _drawWicks({
    renderingScope,
    bars,
    visibleRange
  }) {
    if (this._data === null || this._options === null) {
      return;
    }
    const { context: ctx, horizontalPixelRatio, verticalPixelRatio } = renderingScope;
    const wickWidth = gridAndCrosshairMediaWidth(horizontalPixelRatio);
    for (let i = visibleRange.from; i < visibleRange.to; i++) {
      const bar = bars[i];
      ctx.fillStyle = bar.isUp ? this._options.wickUpColor : this._options.wickDownColor;
      const verticalPositions = positionsBox({
        position1Media: bar.lowY,
        position2Media: bar.highY,
        pixelRatio: verticalPixelRatio
      });
      const linePositions = positionsLine({
        positionMedia: bar.x,
        pixelRatio: horizontalPixelRatio,
        desiredWidthMedia: wickWidth
      });
      ctx.fillRect(linePositions.position, verticalPositions.position, linePositions.length, verticalPositions.length);
    }
  }
  _drawCandles({
    renderingScope,
    bars,
    visibleRange,
    radius
  }) {
    if (this._data === null || this._options === null) {
      return;
    }
    const { context: ctx, horizontalPixelRatio, verticalPixelRatio } = renderingScope;
    const candleBodyWidth = candlestickWidth(this._data.barSpacing, 1);
    for (let i = visibleRange.from; i < visibleRange.to; i++) {
      const bar = bars[i];
      const verticalPositions = positionsBox({
        position1Media: Math.min(bar.openY, bar.closeY),
        position2Media: Math.max(bar.openY, bar.closeY),
        pixelRatio: verticalPixelRatio
      });
      const linePositions = positionsLine({
        positionMedia: bar.x,
        pixelRatio: horizontalPixelRatio,
        desiredWidthMedia: candleBodyWidth
      });
      ctx.fillStyle = bar.isUp ? this._options.upColor : this._options.downColor;
      roundRect({
        ctx,
        x: linePositions.position,
        y: verticalPositions.position,
        w: linePositions.length,
        h: Math.max(verticalPositions.length, 1),
        radii: radius
      });
    }
  }
}
function optimalCandlestickWidth(barSpacing, pixelRatio) {
  const barSpacingSpecialCaseFrom = 2.5;
  const barSpacingSpecialCaseTo = 4;
  const barSpacingSpecialCaseCoeff = 3;
  if (barSpacing >= barSpacingSpecialCaseFrom && barSpacing <= barSpacingSpecialCaseTo) {
    return Math.floor(barSpacingSpecialCaseCoeff * pixelRatio);
  }
  const barSpacingReducingCoeff = 0.2;
  const coeff = 1 - barSpacingReducingCoeff * Math.atan(Math.max(barSpacingSpecialCaseTo, barSpacing) - barSpacingSpecialCaseTo) / (Math.PI * 0.5);
  const res = Math.floor(barSpacing * coeff * pixelRatio);
  const scaledBarSpacing = Math.floor(barSpacing * pixelRatio);
  const optimal = Math.min(res, scaledBarSpacing);
  return Math.max(Math.floor(pixelRatio), optimal);
}
function candlestickWidth(barSpacing, horizontalPixelRatio) {
  let width = optimalCandlestickWidth(barSpacing, horizontalPixelRatio);
  if (width >= 2) {
    const wickWidth = Math.floor(horizontalPixelRatio);
    if (wickWidth % 2 !== width % 2) {
      width--;
    }
  }
  return width;
}
function gridAndCrosshairBitmapWidth(horizontalPixelRatio) {
  return Math.max(1, Math.floor(horizontalPixelRatio));
}
function gridAndCrosshairMediaWidth(horizontalPixelRatio) {
  return gridAndCrosshairBitmapWidth(horizontalPixelRatio) / horizontalPixelRatio;
}
